import React, { useState, useMemo, useEffect, useRef } from 'react';
import { API_BASE_URL, AUTHORIZED_ROLES } from '../constants/property';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Grid from '../components/Grid';
import Pagination from '../components/Pagination';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { Snackbar, Alert } from '@mui/material';
import { formatIndianGstNumber, isValidIndianGstNumber } from '../utils/gst';
import {
  parseRecordDateTime,
  compareRecordsNewestFirst,
  defaultSortDirectionForColumn,
} from '../utils/tableSort';

const ModalSection = ({ children }) => (
  <section className="mb-6 last:mb-0">
    {children}
  </section>
);

const AmountWithGstHint = ({ gstPercentage, withGST }) => {
  const pct = String(gstPercentage ?? '').trim().replace(/%$/, '').replace(/,/g, '');
  if (!pct || !Number.isFinite(parseFloat(pct)) || !withGST) return null;
  return (
    <p className="mt-1 text-xs text-gray-500">Amount with GST: {withGST}</p>
  );
};

const ADD_BANK_VALUE = '__add_bank__';
const MAINTENANCE_BANK_LIST_URL = `${API_BASE_URL}/api/maintence/Bank`;
const MAINTENANCE_URL = `${API_BASE_URL}/api/maintence`;

const Vendors = () => {
  const location = useLocation();
  const { hasAnyRole } = useAuth();
  const isAdministrator = hasAnyRole(AUTHORIZED_ROLES);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  // Report filters
  const [datePreset, setDatePreset] = useState(''); // today|yesterday|last7|last30|thisMonth|lastMonth|custom
  const [dateDisplay, setDateDisplay] = useState('');
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [showCustomCalendar, setShowCustomCalendar] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedMaterialSupplied, setSelectedMaterialSupplied] = useState('all');
  const [selectedSupplyType, setSelectedSupplyType] = useState('all');
  const [selectedInvoiceStatus, setSelectedInvoiceStatus] = useState('all');
  const [selectedGstNo, setSelectedGstNo] = useState('all');
  const [selectedModeOfPayment, setSelectedModeOfPayment] = useState('all');
  const [vendorNameInputValue, setVendorNameInputValue] = useState('');
  const [materialSuppliedInputValue, setMaterialSuppliedInputValue] = useState('');
  const [showMaterialSuppliedDropdown, setShowMaterialSuppliedDropdown] = useState(false);
  const [supplyTypeInputValue, setSupplyTypeInputValue] = useState('');
  const [showSupplyTypeDropdown, setShowSupplyTypeDropdown] = useState(false);
  const [invoiceStatusInputValue, setInvoiceStatusInputValue] = useState('');
  const [showInvoiceStatusDropdown, setShowInvoiceStatusDropdown] = useState(false);
  const [gstNoInputValue, setGstNoInputValue] = useState('');
  const [showGstNoDropdown, setShowGstNoDropdown] = useState(false);
  const [modeOfPaymentInputValue, setModeOfPaymentInputValue] = useState('');
  const [showModeOfPaymentDropdown, setShowModeOfPaymentDropdown] = useState(false);
  
  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  /** GST % from gstPercentage field — same inclusive formula as StockIn: total × (1 + GST%/100). */
  const parseVendorGstPercent = (raw) => {
    const s = String(raw ?? '').trim().replace(/%$/, '').replace(/,/g, '');
    if (!s) return null;
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  };

  const computeVendorAmountWithGST = (totalAmountStr, gstPercentageStr) => {
    const a = parseFloat(String(totalAmountStr ?? '').replace(/[₹,]/g, ''));
    if (!Number.isFinite(a) || a <= 0) return '';
    const pct = parseVendorGstPercent(gstPercentageStr);
    if (pct == null) return '';
    return (a * (1 + pct / 100)).toFixed(2);
  };

  /** Balance = With GST − Advance when GST(%) is set; otherwise Total Amount − Advance. */
  const computeVendorBalance = (totalAmountStr, advanceStr, gstPercentageStr) => {
    const advance = parseFloat(String(advanceStr ?? '').replace(/[₹,]/g, '')) || 0;
    const total = parseFloat(String(totalAmountStr ?? '').replace(/[₹,]/g, '')) || 0;
    const withGST = computeVendorAmountWithGST(totalAmountStr, gstPercentageStr);
    const base = withGST !== '' ? parseFloat(withGST) : total;
    return Math.round((base - advance) * 100) / 100;
  };

  const isAdvanceFilled = (advanceStr) => {
    const s = String(advanceStr ?? '').trim();
    return s !== '' && s !== '-';
  };

  const resolveVendorBalance = (totalAmountStr, advanceStr, gstPercentageStr) => {
    if (!isAdvanceFilled(advanceStr)) return '';
    const total = parseFloat(String(totalAmountStr ?? '').replace(/[₹,]/g, ''));
    const advance = parseFloat(String(advanceStr ?? '').replace(/[₹,]/g, ''));
    if (!Number.isFinite(total) || !Number.isFinite(advance)) return '';
    const b = computeVendorBalance(totalAmountStr, advanceStr, gstPercentageStr);
    return Number.isFinite(b) ? String(b) : '';
  };

  const formatModeOfPaymentDisplay = (mode) => String(mode ?? '').trim();
  const [formData, setFormData] = useState({
    date: getTodayDate(),
    contactName: '',
    modeOfPayment: '',
    transactionId: '',
    chequeNo: '',
    transactionDetails: '',
    bank: '',
    customBank: '',
    vendorName: '',
    contactNumber: '',
    materialSupplied: '',
    supplyType: '',
    advance: '',
    totalAmount: '',
    withGST: '',
    balance: '',
    invoiceStatus: '',
    invoiceNo: '',
    gstNo: '',
    gstPercentage: '',
    comments: ''
  });
  const [showFilters, setShowFilters] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [maintenanceBankOptions, setMaintenanceBankOptions] = useState([]);
  const [sortColumn, setSortColumn] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const defaultVisibleColumns = {
    id: true,
    date: true,
    vendorName: true,
    contactName: true,
    contactNumber: true,
    materialSupplied: true,
    supplyType: true,
    advance: true,
    totalAmount: true,
    gstPercentage: true,
    withGST: true,
    balance: true,
    invoiceStatus: true,
    invoiceNo: true,
    gstNo: true,
    modeOfPayment: true,
    transactionId: true,
    chequeNo: true,
    transactionDetails: true,
    bank: true,
    dateOfTranscation: true,
    comments: true,
    actions: true
  };
  const [visibleColumns, setVisibleColumns] = useState(defaultVisibleColumns);
  const [vendors, setVendors] = useState([]);
  const [maintenanceVendorOptions, setMaintenanceVendorOptions] = useState([]);

  const columnMenuRef = useRef(null);

  // Column definitions for visibility toggle
  const columnDefinitions = [
    { key: 'id', label: 'ID', sortable: true },
    { key: 'date', label: 'Date', sortable: true },
    { key: 'vendorName', label: 'Vendor Name', sortable: true },
    { key: 'contactName', label: 'Contact Name', sortable: true },
    { key: 'contactNumber', label: 'Contact Number', sortable: true },
    { key: 'materialSupplied', label: 'Material Supplied', sortable: true },
    { key: 'supplyType', label: 'Supply Type', sortable: true },
    { key: 'advance', label: 'Advance', sortable: true },
    { key: 'totalAmount', label: 'Total Amount', sortable: true },
    { key: 'gstPercentage', label: 'GST(%)', sortable: true },
    { key: 'withGST', label: 'With GST', sortable: true },
    { key: 'balance', label: 'Balance', sortable: true },
    { key: 'invoiceStatus', label: 'Invoice Status', sortable: true },
    { key: 'invoiceNo', label: 'Invoice No', sortable: true },
    { key: 'gstNo', label: 'GST No', sortable: true },
    { key: 'modeOfPayment', label: 'Mode of Payment', sortable: false },
    { key: 'transactionId', label: 'Transaction ID', sortable: false },
    { key: 'chequeNo', label: 'Credit Note', sortable: false },
    { key: 'transactionDetails', label: 'Transaction Details', sortable: false },
    { key: 'bank', label: 'Bank', sortable: false },
    { key: 'dateOfTranscation', label: 'Date of Transaction', sortable: true },
    { key: 'comments', label: 'Comments', sortable: false },
    { key: 'actions', label: 'Actions', sortable: false }
  ];

  // Helper function to transform API data to component structure
  const transformApiData = (apiData) => {
    if (!Array.isArray(apiData)) {
      return [];
    }

    const formatDate = (dateString) => {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString; // Return original if invalid
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${months[date.getMonth()]} ${String(date.getDate()).padStart(2, '0')}, ${date.getFullYear()}`;
      } catch (e) {
        return dateString;
      }
    };

    const formatInvoiceStatus = (status) => {
      if (!status) return '';
      const statusMap = {
        'PENDING': 'Pending',
        'PAID': 'Paid',
        'OVERDUE': 'Overdue'
      };
      return statusMap[status.toUpperCase()] || status;
    };

    const formatDateOfTranscation = (val) => {
      if (!val) return '';
      const d = new Date(val);
      return isNaN(d.getTime()) ? '' : formatDate(d.toISOString().split('T')[0]);
    };

    const records = apiData.map((item, index) => ({
      id: item.id != null ? item.id : (index + 1).toString(),
      apiIndex: index,
      rawDate: item.date || '',
      date: formatDate(item.date),
      vendors: item.vendors || '',
      vendorName: item.vendorName || '',
      contactName: item.contactName || '',
      contactNumber: item.contactNumber || '',
      materialSupplied: item.materialSupplied || '',
      supplyType: item.supplyType || '',
      advance: item.advance != null ? String(item.advance) : '',
      totalAmount: item.totalAmount != null ? String(item.totalAmount) : '',
      withGST: computeVendorAmountWithGST(item.totalAmount, item.gstPercentage),
      balance:
        isAdvanceFilled(item.advance)
          ? item.balance != null && item.balance !== ''
            ? String(item.balance)
            : resolveVendorBalance(item.totalAmount, item.advance, item.gstPercentage)
          : '',
      invoiceStatus: formatInvoiceStatus(item.invoiceStatus),
      invoiceNo: item.invoiceNo || '',
      gstNo: item.gstNumber || item.gstNo || '',
      gstPercentage:
        item.gstPercentage != null && item.gstPercentage !== ''
          ? String(item.gstPercentage)
          : '',
      modeOfPayment: item.modeOfPayment || '',
      transactionDetails: item.transactionDetails || '',
      bank: item.bank || '',
      chequeNo: item.chequeNo || '',
      transactionId: item.transcationId != null ? String(item.transcationId) : (item.transactionId != null ? String(item.transactionId) : ''),
      dateOfTranscation: formatDateOfTranscation(item.dateOfTranscation),
      dateOfTranscationRaw: item.dateOfTranscation || '',
      amount: item.amount != null ? String(item.amount) : '',
      comments: item.comments || ''
    }));

    records.sort(compareRecordsNewestFirst);
    return records;
  };

  // Fetch vendors from API
  const fetchVendors = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/vendor`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Vendors API Response:', data);
      
      // Transform API data to match component structure
      const transformedData = transformApiData(data);
      setVendors(transformedData);
      
    } catch (err) {
      console.error('Error fetching vendors:', err);
      setError(err.message || 'Failed to fetch vendors');
      // Keep existing data if fetch fails (fallback to static data)
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch vendors on component mount
  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchMaintenanceBanks = async () => {
    try {
      const response = await fetch(MAINTENANCE_BANK_LIST_URL, { method: 'GET', mode: 'cors' });
      if (!response.ok) return;
      const data = await response.json();
      const list = Array.isArray(data) ? data : [];
      const seen = new Set();
      const options = list
        .filter((item) => !item.type || String(item.type).toLowerCase() === 'bank')
        .map((item) => {
          const label = String(item.value ?? item.code ?? '').trim();
          if (!label) return null;
          const key = label.toLowerCase();
          if (seen.has(key)) return null;
          seen.add(key);
          return {
            id: item.id,
            value: label,
            label,
            code: String(item.code ?? item.value ?? '').trim(),
          };
        })
        .filter(Boolean)
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
      setMaintenanceBankOptions(options);
    } catch (err) {
      console.error('Error fetching banks:', err);
    }
  };

  const postMaintenanceBank = async (bankName) => {
    const trimmed = String(bankName || '').trim();
    if (!trimmed) return;

    const response = await fetch(MAINTENANCE_URL, {
      method: 'POST',
      mode: 'cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'Bank',
        value: trimmed,
        code: trimmed,
      }),
    });

    if (!response.ok) {
      const errText = await response.text().catch(() => '');
      throw new Error(errText || `HTTP ${response.status}: ${response.statusText}`);
    }
  };

  useEffect(() => {
    fetchMaintenanceBanks();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/maintence/Vendor`, {
          method: 'GET',
          mode: 'cors'
        });
        if (!response.ok) return;
        const data = await response.json();
        const list = Array.isArray(data) ? data : [];
        const seen = new Set();
        const options = list
          .filter((item) => !item.type || String(item.type).toLowerCase() === 'vendor')
          .map((item) => {
            const label = String(item.value ?? item.code ?? '').trim();
            if (!label) return null;
            const key = label.toLowerCase();
            if (seen.has(key)) return null;
            seen.add(key);
            return { value: label, label };
          })
          .filter(Boolean)
          .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
        if (!cancelled) setMaintenanceVendorOptions(options);
      } catch {
        /* keep empty options on API failure */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Check if modal should be opened from navigation
  useEffect(() => {
    if (location.state?.openModal) {
      setIsModalOpen(true);
      // Clear the state to prevent reopening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Filter options
  const materialSuppliedOptions = [
    { value: 'Concrete', label: 'Concrete' },
    { value: 'Steel', label: 'Steel' },
    { value: 'Bricks', label: 'Bricks' },
    { value: 'Cement', label: 'Cement' },
    { value: 'Glass', label: 'Glass' },
    { value: 'Stone', label: 'Stone' },
    { value: 'Tiles', label: 'Tiles' },
    { value: 'Aggregates', label: 'Aggregates' },
    { value: 'Electrical Materials', label: 'Electrical Materials' },
    { value: 'Roofing Materials', label: 'Roofing Materials' },
  ];

  const supplyTypeOptions = [
    { value: 'In', label: 'In' },
    { value: 'Out', label: 'Out' },
  ];

  const invoiceStatusOptions = [
    { value: 'Paid', label: 'Paid' },
    { value: 'Pending', label: 'Pending' },
    { value: 'Overdue', label: 'Overdue' },
  ];

  const modeOfPaymentOptions = [
    { value: 'Cash', label: 'Cash' },
    { value: 'Cheque', label: 'Cheque' },
    { value: 'UPI', label: 'UPI' },
    { value: 'Online Bank', label: 'Online Bank' },
  ];

  const modalVendorOptions = useMemo(
    () => maintenanceVendorOptions.map((opt) => opt.value),
    [maintenanceVendorOptions]
  );

  const bankOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    const addOption = (value, label) => {
      const key = String(value).trim().toLowerCase();
      if (!key || seen.has(key)) return;
      seen.add(key);
      options.push({ value: String(value).trim(), label: label || String(value).trim() });
    };

    maintenanceBankOptions.forEach((opt) => addOption(opt.value, opt.label));

    if (
      formData.bank &&
      formData.bank !== ADD_BANK_VALUE &&
      !seen.has(String(formData.bank).trim().toLowerCase())
    ) {
      addOption(formData.bank, formData.bank);
    }

    return options;
  }, [maintenanceBankOptions, formData.bank]);

  const resolveBankForSubmit = () => {
    if (formData.bank === ADD_BANK_VALUE) {
      return String(formData.customBank || '').trim();
    }
    return String(formData.bank || '').trim();
  };

  // Build filterable option lists
  const materialSuppliedFilterOptions = useMemo(() => {
    const uniqueMaterials = [...new Set(vendors.map(item => item.materialSupplied))];
    return [{ value: 'all', label: 'All' }, ...uniqueMaterials.map(m => ({ value: m, label: m }))];
  }, [vendors]);

  const supplyTypeFilterOptions = useMemo(() => {
    return [{ value: 'all', label: 'All' }, ...supplyTypeOptions];
  }, []);

  const invoiceStatusFilterOptions = useMemo(() => {
    return [{ value: 'all', label: 'All' }, ...invoiceStatusOptions];
  }, []);

  const gstNoOptions = useMemo(() => {
    const uniqueGstNos = [...new Set(vendors.map(item => item.gstNo))];
    return [{ value: 'all', label: 'All' }, ...uniqueGstNos.map(g => ({ value: g, label: g }))];
  }, [vendors]);

  const modeOfPaymentFilterOptions = useMemo(() => {
    return [{ value: 'all', label: 'All' }, ...modeOfPaymentOptions];
  }, []);

  // Check if any filters are applied
  const hasActiveFilters = useMemo(() => {
    return datePreset !== '' || dateDisplay !== '' || vendorNameInputValue.trim() !== '' || selectedMaterialSupplied !== 'all' || selectedSupplyType !== 'all' || selectedInvoiceStatus !== 'all' || selectedGstNo !== 'all' || selectedModeOfPayment !== 'all';
  }, [datePreset, dateDisplay, vendorNameInputValue, selectedMaterialSupplied, selectedSupplyType, selectedInvoiceStatus, selectedGstNo, selectedModeOfPayment]);

  const formatDateMMMDDYYYY = (dateValue) => {
    if (!dateValue || dateValue === '-') return '-';
    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) return dateValue;
    return parsedDate.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
  };

  const parseDateValue = (dateValue) => {
    if (!dateValue) return new Date(0);
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      const [year, month, day] = dateValue.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    const parsed = new Date(dateValue);
    return Number.isNaN(parsed.getTime())
      ? new Date(0)
      : new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  };

  const isDateInRange = (date, startDate, endDate) => {
    if (!startDate || !endDate || !date) return false;
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const s = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const e = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    return d >= s && d <= e;
  };

  // Compute filtered and sorted data
  const filteredData = useMemo(() => {
    let startDate = null;
    let endDate = null;
    const today = new Date();

    if (datePreset && datePreset !== 'custom') {
      switch (datePreset) {
        case 'today': {
          const d = new Date(today.getFullYear(), today.getMonth(), today.getDate());
          startDate = d;
          endDate = d;
          break;
        }
        case 'yesterday': {
          const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
          startDate = d;
          endDate = d;
          break;
        }
        case 'last7': {
          endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
          startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6);
          break;
        }
        case 'last30': {
          endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
          startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29);
          break;
        }
        case 'thisMonth': {
          startDate = new Date(today.getFullYear(), today.getMonth(), 1);
          endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
          break;
        }
        case 'lastMonth': {
          startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          endDate = new Date(today.getFullYear(), today.getMonth(), 0);
          break;
        }
        default:
          break;
      }
    } else if (datePreset === 'custom' && customStartDate && customEndDate) {
      startDate = customStartDate;
      endDate = customEndDate;
    }

    let filtered = vendors.filter(item => {
      const vendorNameSearchTerm = String(vendorNameInputValue ?? '').trim().toLowerCase();
      const vendorNameOk = !vendorNameSearchTerm || (item.vendorName || '').toString().toLowerCase().includes(vendorNameSearchTerm);
      const materialOk = selectedMaterialSupplied === 'all' || (item.materialSupplied || '').toString().toLowerCase() === selectedMaterialSupplied.toString().toLowerCase();
      const supplyTypeOk = selectedSupplyType === 'all' || (item.supplyType || '').toString().toLowerCase() === selectedSupplyType.toString().toLowerCase();
      const invoiceStatusOk = selectedInvoiceStatus === 'all' || (item.invoiceStatus || '').toString().toLowerCase() === selectedInvoiceStatus.toString().toLowerCase();
      const gstNoOk = selectedGstNo === 'all' || (item.gstNo || '').toString().toLowerCase() === selectedGstNo.toString().toLowerCase();
      const modeOfPaymentOk = selectedModeOfPayment === 'all' || (item.modeOfPayment || '').toString().toLowerCase() === selectedModeOfPayment.toString().toLowerCase();
      let dateOk = true;
      if (startDate && endDate) {
        const itemDate = parseDateValue(item.date);
        dateOk = isDateInRange(itemDate, startDate, endDate);
      }
      return vendorNameOk && materialOk && supplyTypeOk && invoiceStatusOk && gstNoOk && modeOfPaymentOk && dateOk;
    });

    // Apply sorting
    if (sortColumn) {
      filtered = [...filtered].sort((a, b) => {
        let aValue = a[sortColumn];
        let bValue = b[sortColumn];

        // Handle different data types
        if (sortColumn === 'date') {
          aValue = parseRecordDateTime(a.rawDate || aValue);
          bValue = parseRecordDateTime(b.rawDate || bValue);
        } else if (sortColumn === 'dateOfTranscation') {
          aValue = parseRecordDateTime(a.dateOfTranscationRaw || aValue);
          bValue = parseRecordDateTime(b.dateOfTranscationRaw || bValue);
        } else if (sortColumn === 'advance' || sortColumn === 'totalAmount' || sortColumn === 'withGST' || sortColumn === 'balance' || sortColumn === 'gstPercentage') {
          aValue = parseFloat((aValue || '').toString().replace(/[₹,%]/g, '')) || 0;
          bValue = parseFloat((bValue || '').toString().replace(/[₹,%]/g, '')) || 0;
        } else {
          // Handle string values
          aValue = (aValue || '').toString().toLowerCase();
          bValue = (bValue || '').toString().toLowerCase();
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return compareRecordsNewestFirst(a, b);
      });
    }

    return filtered;
  }, [vendors, vendorNameInputValue, selectedMaterialSupplied, selectedSupplyType, selectedInvoiceStatus, selectedGstNo, selectedModeOfPayment, datePreset, customStartDate, customEndDate, sortColumn, sortDirection]);

  // Pagination logic (based on filtered results)
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedVendors = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, itemsPerPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column; date columns default to newest-first
      setSortColumn(column);
      setSortDirection(defaultSortDirectionForColumn(column));
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return (
        <svg className="w-4 h-4 text-white opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    if (sortDirection === 'asc') {
      return (
        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      );
    }
    return (
      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  const toggleColumn = (columnKey) => {
    const visibleCount = getVisibleColumnCount();
    if (visibleColumns[columnKey] && visibleCount <= 1) {
      // Prevent hiding the last visible column
      return;
    }
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };
  const handleResetColumns = () => {
    setVisibleColumns(defaultVisibleColumns);
  };

  const getVisibleColumnCount = () => {
    return Object.values(visibleColumns).filter(Boolean).length;
  };

  // Close column menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(event.target)) {
        setShowColumnMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Date preset display and handling
  useEffect(() => {
    if (!datePreset) {
      setDateDisplay('');
      return;
    }
    
    const now = new Date();
    const toDisplay = (d) => d.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
    const clone = (d) => new Date(d.getTime());
    let start = clone(now);
    let end = clone(now);
    
    switch (datePreset) {
      case 'today':
        break;
      case 'yesterday':
        start.setDate(start.getDate() - 1); 
        end.setDate(end.getDate() - 1); 
        break;
      case 'last7':
        start.setDate(start.getDate() - 6); 
        break;
      case 'last30':
        start.setDate(start.getDate() - 29); 
        break;
      case 'thisMonth':
        start = new Date(now.getFullYear(), now.getMonth(), 1); 
        break;
      case 'lastMonth': {
        const firstLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
        start = firstLastMonth; 
        end = lastLastMonth; 
        break;
      }
      case 'custom':
        if (!dateDisplay) {
          setDateDisplay(`${toDisplay(start)} - ${toDisplay(end)}`);
        }
        return;
      default:
        break;
    }
    setDateDisplay(`${toDisplay(start)} - ${toDisplay(end)}`);
  }, [datePreset]);

  const handleDatePresetChange = (preset) => {
    setDatePreset(preset);
    setShowDateDropdown(false);
    if (preset === 'custom') {
      setShowCustomCalendar(true);
    } else {
      setShowCustomCalendar(false);
    }
  };

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
  };

  const isSameDate = (date1, date2) => {
    if (!date1 || !date2) return false;
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  };

  const handleDateClick = (date) => {
    if (!customStartDate || (customStartDate && customEndDate)) {
      setCustomStartDate(date);
      setCustomEndDate(null);
    } else if (customStartDate && !customEndDate) {
      if (date < customStartDate) {
        setCustomEndDate(customStartDate);
        setCustomStartDate(date);
      } else {
        setCustomEndDate(date);
      }
    }
  };

  const handleApplyCustomRange = () => {
    if (customStartDate && customEndDate) {
      setDateDisplay(`${formatDate(customStartDate)} - ${formatDate(customEndDate)}`);
      setShowCustomCalendar(false);
      setShowDateDropdown(false);
    }
  };

  const handleCancelCustomRange = () => {
    setShowCustomCalendar(false);
    setShowDateDropdown(false);
    setCustomStartDate(null);
    setCustomEndDate(null);
  };

  const handleClearDate = (e) => {
    e.stopPropagation();
    setDatePreset('');
    setDateDisplay('');
    setCustomStartDate(null);
    setCustomEndDate(null);
    setShowCustomCalendar(false);
    setShowDateDropdown(false);
  };


  const handleClearMaterialSupplied = (e) => {
    e.stopPropagation();
    setSelectedMaterialSupplied('all');
    setMaterialSuppliedInputValue('');
    setShowMaterialSuppliedDropdown(false);
    setCurrentPage(1);
  };

  const handleClearSupplyType = (e) => {
    e.stopPropagation();
    setSelectedSupplyType('all');
    setSupplyTypeInputValue('');
    setShowSupplyTypeDropdown(false);
    setCurrentPage(1);
  };

  const handleClearInvoiceStatus = (e) => {
    e.stopPropagation();
    setSelectedInvoiceStatus('all');
    setInvoiceStatusInputValue('');
    setShowInvoiceStatusDropdown(false);
    setCurrentPage(1);
  };

  const handleClearGstNo = (e) => {
    e.stopPropagation();
    setSelectedGstNo('all');
    setGstNoInputValue('');
    setShowGstNoDropdown(false);
    setCurrentPage(1);
  };

  const handleClearModeOfPayment = (e) => {
    e.stopPropagation();
    setSelectedModeOfPayment('all');
    setModeOfPaymentInputValue('');
    setShowModeOfPaymentDropdown(false);
    setCurrentPage(1);
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  const handleVendorNameInputChange = (e) => {
    setVendorNameInputValue(e.target.value);
    setCurrentPage(1);
  };

  const handleClearVendorName = () => {
    setVendorNameInputValue('');
    setCurrentPage(1);
  };

  // Autocomplete handlers for Material Supplied
  const isTypingMaterialSuppliedRef = useRef(false);
  
  useEffect(() => {
    if (!isTypingMaterialSuppliedRef.current) {
      if (selectedMaterialSupplied === 'all') {
        setMaterialSuppliedInputValue('');
      } else {
        const selectedOption = materialSuppliedFilterOptions.find(opt => opt.value === selectedMaterialSupplied);
        if (selectedOption) {
          setMaterialSuppliedInputValue(selectedOption.label);
        } else {
          setMaterialSuppliedInputValue('');
        }
      }
    }
  }, [selectedMaterialSupplied, materialSuppliedFilterOptions]);

  const filteredMaterialSuppliedOptions = useMemo(() => {
    if (!materialSuppliedInputValue.trim()) {
      return materialSuppliedFilterOptions;
    }
    const searchTerm = materialSuppliedInputValue.toLowerCase();
    return materialSuppliedFilterOptions.filter(opt => 
      opt.label.toLowerCase().includes(searchTerm)
    );
  }, [materialSuppliedFilterOptions, materialSuppliedInputValue]);

  const handleMaterialSuppliedInputChange = (e) => {
    const value = e.target.value;
    isTypingMaterialSuppliedRef.current = true;
    setMaterialSuppliedInputValue(value);
    setShowMaterialSuppliedDropdown(true);
    
    if (!value.trim()) {
      setSelectedMaterialSupplied('all');
    }
    
    setTimeout(() => {
      isTypingMaterialSuppliedRef.current = false;
    }, 100);
  };

  const handleMaterialSuppliedSelect = (option) => {
    isTypingMaterialSuppliedRef.current = false;
    setSelectedMaterialSupplied(option.value);
    setMaterialSuppliedInputValue(option.label);
    setShowMaterialSuppliedDropdown(false);
    setCurrentPage(1);
  };

  // Autocomplete handlers for Supply Type
  const isTypingSupplyTypeRef = useRef(false);
  
  useEffect(() => {
    if (!isTypingSupplyTypeRef.current) {
      if (selectedSupplyType === 'all') {
        setSupplyTypeInputValue('');
      } else {
        const selectedOption = supplyTypeFilterOptions.find(opt => opt.value === selectedSupplyType);
        if (selectedOption) {
          setSupplyTypeInputValue(selectedOption.label);
        } else {
          setSupplyTypeInputValue('');
        }
      }
    }
  }, [selectedSupplyType, supplyTypeFilterOptions]);

  const filteredSupplyTypeOptions = useMemo(() => {
    if (!supplyTypeInputValue.trim()) {
      return supplyTypeFilterOptions;
    }
    const searchTerm = supplyTypeInputValue.toLowerCase();
    return supplyTypeFilterOptions.filter(opt => 
      opt.label.toLowerCase().includes(searchTerm)
    );
  }, [supplyTypeFilterOptions, supplyTypeInputValue]);

  const handleSupplyTypeInputChange = (e) => {
    const value = e.target.value;
    isTypingSupplyTypeRef.current = true;
    setSupplyTypeInputValue(value);
    setShowSupplyTypeDropdown(true);
    
    if (!value.trim()) {
      setSelectedSupplyType('all');
    }
    
    setTimeout(() => {
      isTypingSupplyTypeRef.current = false;
    }, 100);
  };

  const handleSupplyTypeSelect = (option) => {
    isTypingSupplyTypeRef.current = false;
    setSelectedSupplyType(option.value);
    setSupplyTypeInputValue(option.label);
    setShowSupplyTypeDropdown(false);
    setCurrentPage(1);
  };

  // Autocomplete handlers for Invoice Status
  const isTypingInvoiceStatusRef = useRef(false);
  
  useEffect(() => {
    if (!isTypingInvoiceStatusRef.current) {
      if (selectedInvoiceStatus === 'all') {
        setInvoiceStatusInputValue('');
      } else {
        const selectedOption = invoiceStatusFilterOptions.find(opt => opt.value === selectedInvoiceStatus);
        if (selectedOption) {
          setInvoiceStatusInputValue(selectedOption.label);
        } else {
          setInvoiceStatusInputValue('');
        }
      }
    }
  }, [selectedInvoiceStatus, invoiceStatusFilterOptions]);

  const filteredInvoiceStatusOptions = useMemo(() => {
    if (!invoiceStatusInputValue.trim()) {
      return invoiceStatusFilterOptions;
    }
    const searchTerm = invoiceStatusInputValue.toLowerCase();
    return invoiceStatusFilterOptions.filter(opt => 
      opt.label.toLowerCase().includes(searchTerm)
    );
  }, [invoiceStatusFilterOptions, invoiceStatusInputValue]);

  const handleInvoiceStatusInputChange = (e) => {
    const value = e.target.value;
    isTypingInvoiceStatusRef.current = true;
    setInvoiceStatusInputValue(value);
    setShowInvoiceStatusDropdown(true);
    
    if (!value.trim()) {
      setSelectedInvoiceStatus('all');
    }
    
    setTimeout(() => {
      isTypingInvoiceStatusRef.current = false;
    }, 100);
  };

  const handleInvoiceStatusSelect = (option) => {
    isTypingInvoiceStatusRef.current = false;
    setSelectedInvoiceStatus(option.value);
    setInvoiceStatusInputValue(option.label);
    setShowInvoiceStatusDropdown(false);
    setCurrentPage(1);
  };

  // Autocomplete handlers for GST No
  const isTypingGstNoRef = useRef(false);
  
  useEffect(() => {
    if (!isTypingGstNoRef.current) {
      if (selectedGstNo === 'all') {
        setGstNoInputValue('');
      } else {
        const selectedOption = gstNoOptions.find(opt => opt.value === selectedGstNo);
        if (selectedOption) {
          setGstNoInputValue(selectedOption.label);
        } else {
          setGstNoInputValue('');
        }
      }
    }
  }, [selectedGstNo, gstNoOptions]);

  const filteredGstNoOptions = useMemo(() => {
    if (!gstNoInputValue.trim()) {
      return gstNoOptions;
    }
    const searchTerm = gstNoInputValue.toLowerCase();
    return gstNoOptions.filter(opt => 
      opt.label.toLowerCase().includes(searchTerm)
    );
  }, [gstNoOptions, gstNoInputValue]);

  const handleGstNoInputChange = (e) => {
    const value = e.target.value;
    isTypingGstNoRef.current = true;
    setGstNoInputValue(value);
    setShowGstNoDropdown(true);
    
    if (!value.trim()) {
      setSelectedGstNo('all');
    }
    
    setTimeout(() => {
      isTypingGstNoRef.current = false;
    }, 100);
  };

  const handleGstNoSelect = (option) => {
    isTypingGstNoRef.current = false;
    setSelectedGstNo(option.value);
    setGstNoInputValue(option.label);
    setShowGstNoDropdown(false);
    setCurrentPage(1);
  };

  // Autocomplete handlers for Mode of Payment
  const isTypingModeOfPaymentRef = useRef(false);
  
  useEffect(() => {
    if (!isTypingModeOfPaymentRef.current) {
      if (selectedModeOfPayment === 'all') {
        setModeOfPaymentInputValue('');
      } else {
        const selectedOption = modeOfPaymentFilterOptions.find(opt => opt.value === selectedModeOfPayment);
        if (selectedOption) {
          setModeOfPaymentInputValue(selectedOption.label);
        } else {
          setModeOfPaymentInputValue('');
        }
      }
    }
  }, [selectedModeOfPayment, modeOfPaymentFilterOptions]);

  const filteredModeOfPaymentOptions = useMemo(() => {
    if (!modeOfPaymentInputValue.trim()) {
      return modeOfPaymentFilterOptions;
    }
    const searchTerm = modeOfPaymentInputValue.toLowerCase();
    return modeOfPaymentFilterOptions.filter(opt => 
      opt.label.toLowerCase().includes(searchTerm)
    );
  }, [modeOfPaymentFilterOptions, modeOfPaymentInputValue]);

  const handleModeOfPaymentInputChange = (e) => {
    const value = e.target.value;
    isTypingModeOfPaymentRef.current = true;
    setModeOfPaymentInputValue(value);
    setShowModeOfPaymentDropdown(true);
    
    if (!value.trim()) {
      setSelectedModeOfPayment('all');
    }
    
    setTimeout(() => {
      isTypingModeOfPaymentRef.current = false;
    }, 100);
  };

  const handleModeOfPaymentSelect = (option) => {
    isTypingModeOfPaymentRef.current = false;
    setSelectedModeOfPayment(option.value);
    setModeOfPaymentInputValue(option.label);
    setShowModeOfPaymentDropdown(false);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setDatePreset('');
    setDateDisplay('');
    setCustomStartDate(null);
    setCustomEndDate(null);
    setSelectedMaterialSupplied('all');
    setSelectedSupplyType('all');
    setSelectedInvoiceStatus('all');
    setSelectedGstNo('all');
    setSelectedModeOfPayment('all');
    setVendorNameInputValue('');
    setMaterialSuppliedInputValue('');
    setSupplyTypeInputValue('');
    setInvoiceStatusInputValue('');
    setGstNoInputValue('');
    setModeOfPaymentInputValue('');
    setCurrentPage(1);
    setShowCustomCalendar(false);
    setShowDateDropdown(false);
    setShowMaterialSuppliedDropdown(false);
    setShowSupplyTypeDropdown(false);
    setShowInvoiceStatusDropdown(false);
    setShowGstNoDropdown(false);
    setShowModeOfPaymentDropdown(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDateDropdown && !event.target.closest('.date-dropdown-container')) {
        setShowDateDropdown(false);
      }
      if (showMaterialSuppliedDropdown && !event.target.closest('.material-supplied-autocomplete-container')) {
        setShowMaterialSuppliedDropdown(false);
      }
      if (showSupplyTypeDropdown && !event.target.closest('.supply-type-autocomplete-container')) {
        setShowSupplyTypeDropdown(false);
      }
      if (showInvoiceStatusDropdown && !event.target.closest('.invoice-status-autocomplete-container')) {
        setShowInvoiceStatusDropdown(false);
      }
      if (showGstNoDropdown && !event.target.closest('.gst-no-autocomplete-container')) {
        setShowGstNoDropdown(false);
      }
      if (showModeOfPaymentDropdown && !event.target.closest('.mode-of-payment-autocomplete-container')) {
        setShowModeOfPaymentDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDateDropdown, showMaterialSuppliedDropdown, showSupplyTypeDropdown, showInvoiceStatusDropdown, showGstNoDropdown, showModeOfPaymentDropdown]);

  const handleExportPDF = () => {
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(16);
    doc.text('Vendors Report', 14, 15);
    
    // Add date range if available
    if (dateDisplay) {
      doc.setFontSize(10);
      doc.text(`Date Range: ${dateDisplay}`, 14, 22);
    }
    
    // Prepare table data
    const tableData = filteredData.map(item => [
      item.id,
      item.date,
      item.vendorName,
      item.contactName,
      item.materialSupplied,
      item.supplyType,
      item.advance,
      item.totalAmount,
      item.gstPercentage != null && item.gstPercentage !== '' ? item.gstPercentage : '',
      item.withGST || '',
      item.balance != null && item.balance !== '' ? item.balance : '',
      item.invoiceStatus,
      item.invoiceNo || '',
      item.gstNo,
      item.modeOfPayment,
      item.transactionDetails,
      item.bank,
      item.chequeNo,
      item.transactionId
    ]);
    
    // Add table
    autoTable(doc, {
      startY: dateDisplay ? 28 : 22,
      head: [['ID', 'Date', 'Vendor Name', 'Contact Name', 'Material Supplied', 'Supply Type', 'Advance', 'Total Amount', 'GST(%)', 'With GST', 'Balance', 'Invoice Status', 'Invoice No', 'GST No', 'Mode of Payment', 'Transaction Details', 'Bank', 'Credit Note', 'Transaction ID']],
      body: tableData,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [0, 150, 136], textColor: 255 },
      margin: { top: 20 }
    });
    
    // Save the PDF
    doc.save(`Vendors_Report_${new Date().toISOString().slice(0,10)}.pdf`);
  };

  const handleExportExcel = () =>{
    const headers = ['ID','Date','Vendor Name','Contact Name','Material Supplied','Supply Type','Advance','Total Amount','GST(%)','With GST','Balance','Invoice Status','Invoice No','GST No','Mode of Payment','Transaction Details','Bank','Credit Note','Transaction ID'];
    const rows = filteredData.map(item => [
      item.id, item.date, item.vendorName, item.contactName, item.materialSupplied, item.supplyType, 
      item.advance, item.totalAmount,
      item.gstPercentage != null && item.gstPercentage !== '' ? item.gstPercentage : '',
      item.withGST || '',
      item.balance != null && item.balance !== '' ? item.balance : '',
      item.invoiceStatus, item.invoiceNo || '', item.gstNo,
      item.modeOfPayment, item.transactionDetails, item.bank, item.chequeNo, item.transactionId
    ]);
    
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Vendors Report');
    
    // Set column widths
    const columnWidths = [
      { wch: 8 }, { wch: 12 }, { wch: 18 }, { wch: 18 }, { wch: 15 }, { wch: 12 },
      { wch: 10 }, { wch: 12 }, { wch: 8 }, { wch: 11 }, { wch: 10 }, { wch: 15 }, { wch: 15 }, { wch: 18 }, { wch: 15 }, { wch: 18 }, { wch: 15 }, { wch: 18 }, { wch: 15 }
    ];
    worksheet['!cols'] = columnWidths;
    
    XLSX.writeFile(workbook, `Vendors_Report_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const handleGenerateReport = () => {
    // Export filtered rows to CSV
    const headers = ['ID','Date','Vendor Name','Contact Name','Material Supplied','Supply Type','Advance','Total Amount','GST(%)','With GST','Balance','Invoice Status','Invoice No','GST No','Mode of Payment','Transaction Details','Bank','Credit Note','Transaction ID'];
    const rows = filteredData.map(item => [
      item.id, item.date, item.vendorName, item.contactName, item.materialSupplied, item.supplyType, 
      item.advance, item.totalAmount,
      item.gstPercentage != null && item.gstPercentage !== '' ? item.gstPercentage : '',
      item.withGST || '',
      item.balance != null && item.balance !== '' ? item.balance : '',
      item.invoiceStatus, item.invoiceNo || '', item.gstNo,
      item.modeOfPayment, item.transactionDetails, item.bank, item.chequeNo, item.transactionId
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => (v == null ? '' : String(v).replace(/"/g,'""'))).map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Vendors_Report_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: name === 'gstNo' ? formatIndianGstNumber(value) : value
      };
      if (name === 'totalAmount' || name === 'advance' || name === 'gstPercentage') {
        const totalStr = name === 'totalAmount' ? value : newData.totalAmount;
        const advanceStr = name === 'advance' ? value : newData.advance;
        const gstPctStr = name === 'gstPercentage' ? value : newData.gstPercentage;
        newData.balance = resolveVendorBalance(totalStr, advanceStr, gstPctStr);
        newData.withGST = computeVendorAmountWithGST(totalStr, gstPctStr);
      }
      return newData;
    });
  };

  const handleBankChange = (e) => {
    const { value } = e.target;
    if (value === ADD_BANK_VALUE) {
      setFormData((prev) => ({
        ...prev,
        bank: ADD_BANK_VALUE,
        customBank: prev.customBank || '',
      }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      bank: value,
      customBank: '',
    }));
  };

  const handleNumberInput = (e) => {
    // Allow: backspace, delete, tab, escape, enter, decimal point
    if ([46, 8, 9, 27, 13, 110, 190].indexOf(e.keyCode) !== -1 ||
      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true) ||
      // Allow: home, end, left, right
      (e.keyCode >= 35 && e.keyCode <= 39)) {
      return;
    }
    // Ensure that it is a number and stop the keypress
    if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
      e.preventDefault();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.vendorName) {
      alert('Please enter Vendor Name');        
      return;
    }
    
    if (!formData.contactNumber) {
      alert('Please enter Contact Number');
      return;
    }
    
    if (!formData.materialSupplied) {
      alert('Please enter Material Supplied');
      return;
    }
    
    if (!formData.supplyType) {
      alert('Please select Supply Type');
      return;
    }
    

    if (!formData.totalAmount) {
      alert('Please enter Total Amount');
      return;
    }
    
    if (!formData.invoiceStatus) {
      alert('Please select Invoice Status');
      return;
    }
    


    const resolvedBank = resolveBankForSubmit();
    if (!resolvedBank) {
      alert(formData.bank === ADD_BANK_VALUE ? 'Please enter Bank name' : 'Please select Bank');
      return;
    }
    
    // Validate Transaction ID/Cheque No based on Mode of Payment
    // Only required if mode is Cash (Cheque), Bank (UPI), or Credit (Online Bank)
    if ((formData.modeOfPayment === 'Cheque' || formData.modeOfPayment === 'UPI' || formData.modeOfPayment === 'Online Bank') && !String(formData.transactionId || '').trim()) {
      alert(`Please enter ${formData.modeOfPayment === 'Cheque' ? 'Credit Note' : 'Transaction ID'}`);
      return;
    }

    // When Add Bank is used, save the new bank via maintenance API before vendor save
    if (formData.bank === ADD_BANK_VALUE) {
      const bankExists = bankOptions.some(
        (opt) => opt.value.toLowerCase() === resolvedBank.toLowerCase()
      );
      if (!bankExists) {
        try {
          await postMaintenanceBank(resolvedBank);
          await fetchMaintenanceBanks();
        } catch (err) {
          console.error('Error adding bank:', err);
          alert(`Failed to add bank: ${err.message || 'Unknown error'}`);
          return;
        }
      }
    }
    
    const advance = parseFloat(formData.advance) || 0;
    const totalAmount = parseFloat(formData.totalAmount) || 0;
    const balance = parseFloat(formData.balance) || 0;
    
    // Map invoice status to uppercase for API
    const invoiceStatusMap = {
      'Paid': 'PAID',
      'Pending': 'PENDING',
      'Overdue': 'OVERDUE'
    };
    
    // Determine chequeNo and transactionId based on mode of payment
    // When mode is "Cheque", the single field holds Cheque No; when Bank/Credit it holds Transaction ID
    const chequeNoValue = formData.modeOfPayment === 'Cheque' ? (formData.transactionId || formData.chequeNo || '') : (formData.chequeNo || '');
    const transactionIdValue = formData.modeOfPayment === 'Cheque' ? '' : (formData.transactionId || '');

    const gstPctRaw = String(formData.gstPercentage ?? '').trim();
    let gstPercentagePayload = null;
    if (gstPctRaw !== '') {
      const n = parseFloat(gstPctRaw);
      gstPercentagePayload = Number.isFinite(n) ? n : null;
    }
    
    try {
      let response;
      let responseData;
      
      if (isEditMode) {
        // Update existing vendor
        if (!editingId) {
          alert('Error: Vendor ID not found');
          return;
        }
        
        // Prepare API payload for update - keys match API (id, vendors, date, vendorName, contactName, materialSupplied, supplyType, advance, totalAmount, balance, invoiceStatus, gstNumber, modeOfPayment, transactionDetails, bank, chequeNo, transcationId, invoiceNo, contactNumber, dateOfTranscation, amount, comments)
        const updatePayload = {
          id: parseInt(editingId) || 0,
          vendors: formData.vendorName || '',
          date: formData.date || new Date().toISOString().split('T')[0],
          vendorName: formData.vendorName || '',
          contactName: formData.contactName || '',
          materialSupplied: formData.materialSupplied || '',
          supplyType: formData.supplyType || '',
          advance: advance,
          totalAmount: totalAmount,
          balance: balance,
          invoiceStatus: invoiceStatusMap[formData.invoiceStatus] || formData.invoiceStatus.toUpperCase(),
          gstNumber: formData.gstNo || '',
          gstPercentage: gstPercentagePayload,
          modeOfPayment: formData.modeOfPayment || '',
          transactionDetails: formData.transactionDetails || '',
          bank: resolvedBank,
          chequeNo: chequeNoValue,
          transcationId: transactionIdValue,
          invoiceNo: formData.invoiceNo || '',
          contactNumber: formData.contactNumber || '',
          dateOfTranscation: formData.date ? new Date(formData.date).toISOString() : new Date().toISOString(),
          amount: formData.totalAmount != null && formData.totalAmount !== '' ? String(formData.totalAmount) : '',
          comments: formData.comments || ''
        };
        
        console.log('Update Request Body:', JSON.stringify(updatePayload, null, 2));
        
        response = await fetch(`${API_BASE_URL}/api/vendor/update`, {
          method: 'PUT',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updatePayload)
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
          throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
        }
        
        responseData = await response.json();
        console.log('Vendor updated successfully:', responseData);
        
        // Refresh the vendor list from API
        await fetchVendors();
        setCurrentPage(1); // Reset to first page
        
        setSuccessMessage('Vendor updated successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        handleClose();
      } else {
        // Create new vendor - payload keys match API (id, vendors, date, vendorName, contactName, materialSupplied, supplyType, advance, totalAmount, balance, invoiceStatus, gstNumber, modeOfPayment, transactionDetails, bank, chequeNo, transcationId, invoiceNo, contactNumber, dateOfTranscation, amount, comments)
        const createPayload = {
          id: 0,
          vendors: formData.vendorName || '',
          date: formData.date || new Date().toISOString().split('T')[0],
          vendorName: formData.vendorName || '',
          contactName: formData.contactName || '',
          materialSupplied: formData.materialSupplied || '',
          supplyType: formData.supplyType || '',
          advance: advance,
          totalAmount: totalAmount,
          balance: balance,
          invoiceStatus: invoiceStatusMap[formData.invoiceStatus] || formData.invoiceStatus.toUpperCase(),
          gstNumber: formData.gstNo || '',
          gstPercentage: gstPercentagePayload,
          modeOfPayment: formData.modeOfPayment || '',
          transactionDetails: formData.transactionDetails || '',
          bank: resolvedBank,
          chequeNo: chequeNoValue,
          transcationId: transactionIdValue,
          invoiceNo: formData.invoiceNo || '',
          contactNumber: formData.contactNumber || '',
          dateOfTranscation: formData.date ? new Date(formData.date).toISOString() : new Date().toISOString(),
          amount: formData.totalAmount != null && formData.totalAmount !== '' ? String(formData.totalAmount) : '',
          comments: formData.comments || ''
        };
        
        response = await fetch(`${API_BASE_URL}/api/vendor/save`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(createPayload)
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
        }
        
        responseData = await response.json();
        console.log('Vendor added successfully:', responseData);
        
        setSuccessMessage('Vendor added successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        handleClose();
        
        // Refresh the vendor list from API
        await fetchVendors();
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'saving'} vendor:`, error);
      setSuccessMessage(`Failed to ${isEditMode ? 'update' : 'add'} vendor: ${error.message}`);
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setEditingId(null);
    setFormData({
      date: getTodayDate(),
      contactName: '',
      modeOfPayment: '',
      transactionId: '',
      chequeNo: '',
      transactionDetails: '',
      bank: '',
      customBank: '',
      vendorName: '',
      contactNumber: '',
      materialSupplied: '',
      supplyType: '',
      advance: '',
      totalAmount: '',
      withGST: '',
      balance: '',
      invoiceStatus: '',
      invoiceNo: '',
      gstNo: '',
      gstPercentage: '',
      comments: ''
    });
  };

  const handleEdit = (id) => {
    const record = vendors.find(item => item.id === id);
    if (record) {
      // Convert date from "Oct 01, 2025" format to YYYY-MM-DD format for input
      const convertDateToInputFormat = (dateStr) => {
        if (!dateStr) return getTodayDate();
        // Check if already in YYYY-MM-DD format
        if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return dateStr;
        }
        // Parse "Oct 01, 2025" format
        const months = {
          'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04',
          'May': '05', 'Jun': '06', 'Jul': '07', 'Aug': '08',
          'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
        };
        const match = dateStr.match(/(\w+)\s+(\d+),\s+(\d+)/);
        if (match) {
          const [, month, day, year] = match;
          const monthNum = months[month] || '01';
          return `${year}-${monthNum}-${day.padStart(2, '0')}`;
        }
        return getTodayDate();
      };

      // When mode is "Cheque", the form uses transactionId field but displays it as "Cheque No"
      const transactionIdValue = record.modeOfPayment === 'Cheque'
        ? (record.chequeNo || record.transactionId || '')
        : (record.transactionId || '');

      setFormData({
        date: convertDateToInputFormat(record.date),
        contactName: record.contactName || '',
        modeOfPayment: record.modeOfPayment || '',
        transactionId: transactionIdValue,
        chequeNo: record.chequeNo || '',
        transactionDetails: record.transactionDetails || '',
        bank: record.bank || '',
        customBank: '',
        vendorName: record.vendorName || record.vendors || '',
        contactNumber: record.contactNumber || '',
        materialSupplied: record.materialSupplied || '',
        supplyType: record.supplyType || '',
        advance: record.advance || '',
        totalAmount: record.totalAmount || '',
        withGST: record.withGST || computeVendorAmountWithGST(record.totalAmount, record.gstPercentage),
        balance: resolveVendorBalance(record.totalAmount, record.advance, record.gstPercentage),
        invoiceStatus: record.invoiceStatus || '',
        invoiceNo: record.invoiceNo || '',
        gstNo: formatIndianGstNumber(record.gstNo || ''),
        gstPercentage: record.gstPercentage != null && record.gstPercentage !== '' ? String(record.gstPercentage) : '',
        comments: record.comments || ''
      });
      setEditingId(id);
      setIsEditMode(true);
      setIsModalOpen(true);
    }
  };

  const handleDelete = (id) => {
    setRecordToDelete(id);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!recordToDelete) return;
    
    try {
      const apiUrl = `${API_BASE_URL}/api/vendor/${recordToDelete}`;
      
      const response = await fetch(apiUrl, {
        method: 'DELETE',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      console.log('Vendor deleted successfully');
      
      // Refresh the vendor list from API
      await fetchVendors();
      
      // Reset to first page
      setCurrentPage(1);
      
      setSuccessMessage('Vendor deleted successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setShowDeleteConfirm(false);
      setRecordToDelete(null);
    } catch (error) {
      console.error('Error deleting vendor:', error);
      setError(error.message || 'Failed to delete vendor');
      setSuccessMessage(`Failed to delete vendor: ${error.message}`);
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      setShowDeleteConfirm(false);
      setRecordToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setRecordToDelete(null);
  };

  return (
    <>
      <div className="space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4 mx-6 mt-4">
            <p className="font-medium">Error loading vendors:</p>
            <p className="text-sm">{error}</p>
            <button
              onClick={fetchVendors}
              className="mt-2 text-sm underline hover:text-red-900"
            >
              Retry
            </button>
          </div>
        )}
        
        <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-poppins">Vendors</h1>
         
        </div>
        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={fetchVendors}
            className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            title="Refresh"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v6h6M20 20v-6h-6M20 8a8 8 0 00-14.828-3M4 16a8 8 0 0014.828 3" />
            </svg>
            
          </button>
          <div className="relative" ref={columnMenuRef}>
            <button
              onClick={() => setShowColumnMenu(!showColumnMenu)}
              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              title="Show/Hide Columns"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              
            </button>
            {showColumnMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-96 overflow-y-auto">
                <div className="p-2">
                  <div className="px-3 py-2 text-xs font-semibold text-gray-700 uppercase border-b border-gray-200">
                    Toggle Columns
                  </div>
                  <div className="px-3 py-2 border-b border-gray-200">
                    <button
                      type="button"
                      onClick={handleResetColumns}
                      className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Reset
                    </button>
                  </div>
                  {columnDefinitions.map((col) => {
                    const isLastVisible = visibleColumns[col.key] && getVisibleColumnCount() === 1;
                    return (
                      <label
                        key={col.key}
                        className={`flex items-center px-3 py-2 hover:bg-gray-100 ${
                          isLastVisible ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                        }`}
                        title={isLastVisible ? 'At least one column must remain visible' : ''}
                      >
                        <input
                          type="checkbox"
                          checked={visibleColumns[col.key]}
                          onChange={() => toggleColumn(col.key)}
                          disabled={isLastVisible}
                          className="mr-2 h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded disabled:cursor-not-allowed"
                        />
                        <span className={`text-sm ${isLastVisible ? 'text-gray-500' : 'text-gray-700'}`}>{col.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          {isAdministrator && (
            <button 
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              title={showFilters ? "Hide Filters" : "Show Filters"}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
              </svg>
              <span>{showFilters ? "Hide Filters" : "Show Filters"}</span>
            </button>
          )}
          <button 
            onClick={() => {
              setIsEditMode(false);
              setFormData(prev => ({
                ...prev,
                date: getTodayDate()
              }));
              setIsModalOpen(true);
            }}
            className="btn-primary"
          >
            Add Vendor
          </button>
        </div>
      </div>

      {isAdministrator && showFilters && (
        <div className="p-4 flex flex-wrap items-end gap-4 bg-white shadow rounded-lg" style={{marginTop: '10px'}}>
            <div className="flex flex-col">
              <label className="text-xs text-gray-600 mb-1">
                {datePreset == '' ? 'Choose Date' : 'Selected Date Range: '} 
                {datePreset && <span className="font-bold text-black text-xs uppercase">({datePreset == "today" ? "Today" : datePreset == "yesterday" ? "Yesterday" : datePreset == "last7" ? "Last 7 Days" : datePreset == "last30" ? "Last 30 Days" : datePreset == "thisMonth" ? "This Month" : datePreset == "lastMonth" ? "Last Month" : datePreset == "custom" ? "Custom Range" : datePreset})</span>}
              </label>
              <div className="relative date-dropdown-container">
                <div className="relative">
                  <input
                    type="text"
                    value={dateDisplay || 'Select Range'}
                    readOnly
                    onClick={() => setShowDateDropdown(!showDateDropdown)}
                    className="w-full px-3 py-2 pl-10 pr-8 border border-gray-300 rounded-md text-sm bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Select Date Range"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  {dateDisplay && (
                    <button
                      type="button"
                      onClick={handleClearDate}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center hover:bg-gray-100 rounded-r-md"
                      title="Clear date"
                    >
                      <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
                {showDateDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg z-20">
                    <div className="py-1">
                      <button
                        onClick={() => handleDatePresetChange('today')}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'today' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                      >
                        Today
                      </button>
                      <button
                        onClick={() => handleDatePresetChange('yesterday')}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'yesterday' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                      >
                        Yesterday
                      </button>
                      <button
                        onClick={() => handleDatePresetChange('last7')}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'last7' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                      >
                        Last 7 Days
                      </button>
                      <button
                        onClick={() => handleDatePresetChange('last30')}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'last30' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                      >
                        Last 30 Days
                      </button>
                      <button
                        onClick={() => handleDatePresetChange('thisMonth')}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'thisMonth' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                      >
                        This Month
                      </button>
                      <button
                        onClick={() => handleDatePresetChange('lastMonth')}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'lastMonth' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                      >
                        Last Month
                      </button>
                      <button
                        onClick={() => handleDatePresetChange('custom')}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${datePreset === 'custom' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'}`}
                      >
                        Custom Range
                      </button>
                    </div>
                  </div>
                )}
                {/* Custom Calendar Modal */}
                {showCustomCalendar && (
                  <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-30 p-4">
                    <div className="flex">
                      {/* Left side - Preset options */}
                      <div className="w-48 pr-4 border-r border-gray-200">
                        <div className="space-y-1">
                          <button
                            onClick={() => handleDatePresetChange('today')}
                            className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'today' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                          >
                            Today
                          </button>
                          <button
                            onClick={() => handleDatePresetChange('yesterday')}
                            className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'yesterday' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                          >
                            Yesterday
                          </button>
                          <button
                            onClick={() => handleDatePresetChange('last7')}
                            className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'last7' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                          >
                            Last 7 Days
                          </button>
                          <button
                            onClick={() => handleDatePresetChange('last30')}
                            className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'last30' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                          >
                            Last 30 Days
                          </button>
                          <button
                            onClick={() => handleDatePresetChange('thisMonth')}
                            className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'thisMonth' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                          >
                            This Month
                          </button>
                          <button
                            onClick={() => handleDatePresetChange('lastMonth')}
                            className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'lastMonth' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                          >
                            Last Month
                          </button>
                          <button
                            onClick={() => handleDatePresetChange('custom')}
                            className={`w-full text-left px-3 py-2 text-sm rounded ${datePreset === 'custom' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'}`}
                          >
                            Custom Range
                          </button>
                        </div>
                      </div>
                      
                      {/* Right side - Calendar */}
                      <div className="ml-4">
                        {/* Calendar Navigation */}
                        <div className="flex items-center justify-between mb-4">
                          <button
                            onClick={() => navigateMonth(-1)}
                            className="p-1 hover:bg-gray-100 rounded"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                          </button>
                          <h3 className="text-sm font-medium text-gray-900">
                            {currentMonth.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                          </h3>
                          <button
                            onClick={() => navigateMonth(1)}
                            className="p-1 hover:bg-gray-100 rounded"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        </div>
                        
                        {/* Calendar Grid */}
                        <div className="w-64">
                          {/* Days of week header */}
                          <div className="grid grid-cols-7 gap-1 mb-2">
                            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                              <div key={day} className="text-xs text-gray-500 text-center py-1">
                                {day}
                              </div>
                            ))}
                          </div>
                          
                          {/* Calendar days */}
                          <div className="grid grid-cols-7 gap-1">
                            {(() => {
                              const daysInMonth = getDaysInMonth(currentMonth);
                              const firstDay = getFirstDayOfMonth(currentMonth);
                              const days = [];
                              
                              // Previous month days
                              const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 0);
                              for (let i = firstDay - 1; i >= 0; i--) {
                                const day = prevMonth.getDate() - i;
                                days.push(
                                  <button
                                    key={`prev-${day}`}
                                    className="text-xs text-gray-400 py-1 hover:bg-gray-100 rounded"
                                    disabled
                                  >
                                    {day}
                                  </button>
                                );
                              }
                              
                              // Current month days
                              for (let day = 1; day <= daysInMonth; day++) {
                                const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
                                const isSelected = isSameDate(date, customStartDate) || isSameDate(date, customEndDate);
                                const isInRange = customStartDate && customEndDate && isDateInRange(date, customStartDate, customEndDate);
                                
                                days.push(
                                  <button
                                    key={day}
                                    onClick={() => handleDateClick(date)}
                                    className={`text-xs py-1 rounded hover:bg-gray-100 ${
                                      isSelected 
                                        ? 'bg-orange-500 text-white' 
                                        : isInRange 
                                        ? 'bg-orange-100 text-orange-600' 
                                        : 'text-gray-700'
                                    }`}
                                  >
                                    {day}
                                  </button>
                                );
                              }
                              
                              // Next month days
                              const remainingCells = 42 - days.length; // 6 rows * 7 days
                              for (let day = 1; day <= remainingCells; day++) {
                                days.push(
                                  <button
                                    key={`next-${day}`}
                                    className="text-xs text-gray-400 py-1 hover:bg-gray-100 rounded"
                                    disabled
                                  >
                                    {day}
                                  </button>
                                );
                              }
                              
                              return days;
                            })()}
                          </div>
                        </div>
                        
                        {/* Selected date range display */}
                        <div className="mt-4 text-center">
                          <div className="text-sm text-gray-600 mb-3">
                            {customStartDate && customEndDate 
                              ? `${formatDate(customStartDate)} - ${formatDate(customEndDate)}`
                              : customStartDate 
                              ? `${formatDate(customStartDate)} - Select end date`
                              : 'Select start date'
                            }
                          </div>
                          
                          {/* Action buttons */}
                          <div className="flex gap-2 justify-center">
                            <button
                              onClick={handleCancelCustomRange}
                              className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleApplyCustomRange}
                              disabled={!customStartDate || !customEndDate}
                              className="px-4 py-2 text-sm bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                            >
                              Apply
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col max-w-[140px]">
              <label className="text-xs text-gray-600 mb-1">Vendor Name</label>
              <div className="relative">
                <input
                  type="text"
                  value={vendorNameInputValue}
                  onChange={handleVendorNameInputChange}
                  placeholder="Search"
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  autoComplete="off"
                />
                <div className="absolute inset-y-0 right-0 flex items-center">
                  {vendorNameInputValue ? (
                    <button
                      type="button"
                      onClick={handleClearVendorName}
                      className="pr-3 hover:bg-gray-100 rounded-r-md flex items-center"
                      title="Clear vendor name"
                    >
                      <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  ) : (
                    <div className="pr-3 pointer-events-none">
                      <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-col max-w-[140px]">
              <label className="text-xs text-gray-600 mb-1">Material Supplied</label>
              <div className="relative material-supplied-autocomplete-container">
                <input
                  type="text"
                  value={materialSuppliedInputValue}
                  onChange={handleMaterialSuppliedInputChange}
                  onFocus={() => {
                    setShowMaterialSuppliedDropdown(true);
                  }}
                  placeholder="Search "
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  autoComplete="off"
                />
                <div className="absolute inset-y-0 right-0 flex items-center">
                  {materialSuppliedInputValue ? (
                    <button
                      type="button"
                      onClick={handleClearMaterialSupplied}
                      className="pr-3 hover:bg-gray-100 rounded-r-md flex items-center"
                      title="Clear material supplied"
                    >
                      <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  ) : (
                    <div className="pr-3 pointer-events-none">
                      <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  )}
                </div>
                {showMaterialSuppliedDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                    <div className="py-1">
                      {filteredMaterialSuppliedOptions.length > 0 ? (
                        filteredMaterialSuppliedOptions.map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => handleMaterialSuppliedSelect(opt)}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 focus:bg-orange-100 focus:outline-none ${
                              selectedMaterialSupplied === opt.value ? 'bg-orange-100 text-orange-600 font-medium' : 'text-gray-700'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-gray-500 text-center">
                          No matches found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col max-w-[140px]">
              <label className="text-xs text-gray-600 mb-1">Supply Type</label>
              <div className="relative supply-type-autocomplete-container">
                <input
                  type="text"
                  value={supplyTypeInputValue}
                  onChange={handleSupplyTypeInputChange}
                  onFocus={() => {
                    setShowSupplyTypeDropdown(true);
                  }}
                  placeholder="Search "
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  autoComplete="off"
                />
                <div className="absolute inset-y-0 right-0 flex items-center">
                  {supplyTypeInputValue ? (
                    <button
                      type="button"
                      onClick={handleClearSupplyType}
                      className="pr-3 hover:bg-gray-100 rounded-r-md flex items-center"
                      title="Clear supply type"
                    >
                      <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  ) : (
                    <div className="pr-3 pointer-events-none">
                      <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  )}
                </div>
                {showSupplyTypeDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                    <div className="py-1">
                      {filteredSupplyTypeOptions.length > 0 ? (
                        filteredSupplyTypeOptions.map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => handleSupplyTypeSelect(opt)}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 focus:bg-orange-100 focus:outline-none ${
                              selectedSupplyType === opt.value ? 'bg-orange-100 text-orange-600 font-medium' : 'text-gray-700'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-gray-500 text-center">
                          No matches found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col max-w-[140px]">
              <label className="text-xs text-gray-600 mb-1">Invoice Status</label>
              <div className="relative invoice-status-autocomplete-container">
                <input
                  type="text"
                  value={invoiceStatusInputValue}
                  onChange={handleInvoiceStatusInputChange}
                  onFocus={() => {
                    setShowInvoiceStatusDropdown(true);
                  }}
                  placeholder="Search "
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  autoComplete="off"
                />
                <div className="absolute inset-y-0 right-0 flex items-center">
                  {invoiceStatusInputValue ? (
                    <button
                      type="button"
                      onClick={handleClearInvoiceStatus}
                      className="pr-3 hover:bg-gray-100 rounded-r-md flex items-center"
                      title="Clear invoice status"
                    >
                      <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  ) : (
                    <div className="pr-3 pointer-events-none">
                      <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  )}
                </div>
                {showInvoiceStatusDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                    <div className="py-1">
                      {filteredInvoiceStatusOptions.length > 0 ? (
                        filteredInvoiceStatusOptions.map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => handleInvoiceStatusSelect(opt)}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 focus:bg-orange-100 focus:outline-none ${
                              selectedInvoiceStatus === opt.value ? 'bg-orange-100 text-orange-600 font-medium' : 'text-gray-700'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-gray-500 text-center">
                          No matches found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
                <div className="flex flex-col max-w-[140px]">
              <label className="text-xs text-gray-600 mb-1">GST No</label>
              <div className="relative gst-no-autocomplete-container">
                <input
                  type="text"
                  value={gstNoInputValue}
                  onChange={handleGstNoInputChange}
                  onFocus={() => {
                    setShowGstNoDropdown(true);
                  }}
                  placeholder="Search "
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  autoComplete="off"
                />
                <div className="absolute inset-y-0 right-0 flex items-center">
                  {gstNoInputValue ? (
                    <button
                      type="button"
                      onClick={handleClearGstNo}
                      className="pr-3 hover:bg-gray-100 rounded-r-md flex items-center"
                      title="Clear GST No"
                    >
                      <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  ) : (
                    <div className="pr-3 pointer-events-none">
                      <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  )}
                </div>
                {showGstNoDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                    <div className="py-1">
                      {filteredGstNoOptions.length > 0 ? (
                        filteredGstNoOptions.map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => handleGstNoSelect(opt)}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 focus:bg-orange-100 focus:outline-none ${
                              selectedGstNo === opt.value ? 'bg-orange-100 text-orange-600 font-medium' : 'text-gray-700'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-gray-500 text-center">
                          No matches found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col max-w-[140px]">
              <label className="text-xs text-gray-600 mb-1">Mode of Payment</label>
              <div className="relative mode-of-payment-autocomplete-container">
                <input
                  type="text"
                  value={modeOfPaymentInputValue}
                  onChange={handleModeOfPaymentInputChange}
                  onFocus={() => {
                    setShowModeOfPaymentDropdown(true);
                  }}
                  placeholder="Search "
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  autoComplete="off"
                />
                <div className="absolute inset-y-0 right-0 flex items-center">
                  {modeOfPaymentInputValue ? (
                    <button
                      type="button"
                      onClick={handleClearModeOfPayment}
                      className="pr-3 hover:bg-gray-100 rounded-r-md flex items-center"
                      title="Clear mode of payment"
                    >
                      <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  ) : (
                    <div className="pr-3 pointer-events-none">
                      <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  )}
                </div>
                {/* {showModeOfPaymentDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                    <div className="py-1">
                      {filteredModeOfPaymentOptions.length > 0 ? (
                        filteredModeOfPaymentOptions.map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => handleModeOfPaymentSelect(opt)}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 focus:bg-orange-100 focus:outline-none ${
                              selectedModeOfPayment === opt.value ? 'bg-orange-100 text-orange-600 font-medium' : 'text-gray-700'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-gray-500 text-center">
                          No matches found
                        </div>
                      )}
                    </div>
                  </div>
                )} */}
              </div>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <button 
                onClick={handleClearFilters}
                disabled={!hasActiveFilters}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                title="Clear Filters"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span>Clear All</span>
              </button>
              <button 
                onClick={handleExportPDF}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                title="Export as PDF"
              >
                <img src="/assets/img/pdf.png" alt="PDF" className="h-4 w-4" />
              </button>
              <button 
                onClick={handleExportExcel}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                title="Export as Excel"
              >
                <img src="/assets/img/sheet.png" alt="Excel" className="h-4 w-4" />
              </button>
            </div>
          </div>
      )}

      {isLoading ? (
        <div className="bg-white shadow rounded-lg p-8 flex items-center justify-center min-h-[400px]">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
            <p className="text-gray-600 text-sm">Loading data...</p>
          </div>
        </div>
      ) : (
        <Grid cols={1} className="bg-white shadow rounded-lg">
          <div className="">
            <div className="overflow-x-auto">
              <div 
                className={`relative overflow-y-auto ${!showFilters ? 'bg-white' : ''}`}
                style={isAdministrator ? { height: 'calc(100vh - 370px)', backgroundColor: '#fff' } :  { height: 'calc(100vh - 270px)', backgroundColor: '#fff' }
                }
              >
                <table className="min-w-full divide-y divide-gray-200 shadow rounded-lg border border-gray-200">
                  <thead className="sticky top-0 z-10" style={{backgroundColor: "rgb(0, 150, 136)"}}>
                  <tr>
                    {/* {visibleColumns.id && (
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('id')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>ID</span>
                          {getSortIcon('id')}
                        </div>
                      </th>
                    )} */}
                    {visibleColumns.date && (
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('date')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Date</span>
                          {getSortIcon('date')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.vendorName && (
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('vendorName')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Vendor Name</span>
                          {getSortIcon('vendorName')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.contactName && (
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('contactName')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Contact Name</span>
                          {getSortIcon('contactName')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.contactNumber && (
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('contactNumber')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Contact Number</span>
                          {getSortIcon('contactNumber')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.materialSupplied && (
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('materialSupplied')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Material Supplied</span>
                          {getSortIcon('materialSupplied')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.supplyType && (
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('supplyType')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Supply Type</span>
                          {getSortIcon('supplyType')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.advance && (
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('advance')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Advance</span>
                          {getSortIcon('advance')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.totalAmount && (
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('totalAmount')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Total Amount</span>
                          {getSortIcon('totalAmount')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.gstPercentage && (
                      <th
                        className="px-6 py-3 text-right text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{ backgroundColor: 'rgb(0, 150, 136)' }}
                        onMouseEnter={(e) => (e.target.style.backgroundColor = 'rgb(0, 130, 120)')}
                        onMouseLeave={(e) => (e.target.style.backgroundColor = 'rgb(0, 150, 136)')}
                        onClick={() => handleSort('gstPercentage')}
                      >
                        <div className="flex items-center justify-end space-x-1">
                          <span>GST(%)</span>
                          {getSortIcon('gstPercentage')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.withGST && (
                      <th
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{ backgroundColor: 'rgb(0, 150, 136)' }}
                        onMouseEnter={(e) => (e.target.style.backgroundColor = 'rgb(0, 130, 120)')}
                        onMouseLeave={(e) => (e.target.style.backgroundColor = 'rgb(0, 150, 136)')}
                        onClick={() => handleSort('withGST')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>With GST</span>
                          {getSortIcon('withGST')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.balance && (
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('balance')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Balance</span>
                          {getSortIcon('balance')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.invoiceStatus && (
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('invoiceStatus')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Invoice Status</span>
                          {getSortIcon('invoiceStatus')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.invoiceNo && (
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('invoiceNo')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Invoice No</span>
                          {getSortIcon('invoiceNo')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.gstNo && (
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('gstNo')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>GST No</span>
                          {getSortIcon('gstNo')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.modeOfPayment && (
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Mode of Payment</th>
                    )}
                    {visibleColumns.transactionId && (
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Transaction ID</th>
                    )}
                    {visibleColumns.chequeNo && (
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Credit Note</th>
                    )}
                    {visibleColumns.transactionDetails && (
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Transaction Details</th>
                    )}
                    {visibleColumns.bank && (
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Bank</th>
                    )}
                    {visibleColumns.dateOfTranscation && (
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                        style={{backgroundColor: "rgb(0, 150, 136)"}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                        onClick={() => handleSort('dateOfTranscation')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Date of Transaction</span>
                          {getSortIcon('dateOfTranscation')}
                        </div>
                      </th>
                    )}
                    {visibleColumns.comments && (
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Comments</th>
                    )}
                    {visibleColumns.actions && (
                      <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" style={{maxHeight: '600px', overflowY: 'auto'}}>
                  {paginatedVendors.length === 0 ? (
                    <tr>
                      <td colSpan={Object.values(visibleColumns).filter(Boolean).length} className="px-6 py-4 text-center text-sm text-gray-500">
                        No vendors found
                      </td>
                    </tr>
                  ) : (
                    paginatedVendors.map((vendor) => (
                  <tr key={vendor.id} className="hover:bg-gray-50">
                    {/* {visibleColumns.id && <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{vendor.id}</td>} */}
                    {visibleColumns.date && <td className="px-6 py-4 whitespace-nowrap text-sm ">{formatDateMMMDDYYYY(vendor.date)}</td>}
                    {visibleColumns.vendorName && <td className="px-6 py-4 whitespace-nowrap text-sm ">{vendor.vendorName}</td>}
                    {visibleColumns.contactName && <td className="px-6 py-4 whitespace-nowrap text-sm ">{vendor.contactName}</td>}
                    {visibleColumns.contactNumber && <td className="px-6 py-4 whitespace-nowrap text-sm ">{vendor.contactNumber || '-'}</td>}
                    {visibleColumns.materialSupplied && <td className="px-6 py-4 whitespace-nowrap text-sm ">{vendor.materialSupplied}</td>}
                    {visibleColumns.supplyType && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm ">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full `}>
                          {vendor.supplyType}
                        </span>
                      </td>
                    )}
                    {visibleColumns.advance && <td className="px-6 py-4 whitespace-nowrap text-sm ">₹{vendor.advance}</td>}
                    {visibleColumns.totalAmount && <td className="px-6 py-4 whitespace-nowrap text-sm ">₹{vendor.totalAmount}</td>}
                    {visibleColumns.gstPercentage && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                        {vendor.gstPercentage != null && vendor.gstPercentage !== ''
                          ? `${String(vendor.gstPercentage).replace(/%$/, '').trim()}%`
                          : '-'}
                      </td>
                    )}
                    {visibleColumns.withGST && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm ">
                        {vendor.withGST ? `₹${vendor.withGST}` : '-'}
                      </td>
                    )}
                    {visibleColumns.balance && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm ">
                        {vendor.balance ? `₹${vendor.balance}` : '-'}
                      </td>
                    )}
                    {visibleColumns.invoiceStatus && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm ">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          vendor.invoiceStatus === 'Paid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {vendor.invoiceStatus}
                        </span>
                      </td>
                    )}
                    {visibleColumns.invoiceNo && <td className="px-6 py-4 whitespace-nowrap text-sm ">{vendor.invoiceNo || '-'}</td>}
                    {visibleColumns.gstNo && <td className="px-6 py-4 whitespace-nowrap text-sm  font-mono">{vendor.gstNo}</td>}
                    {visibleColumns.modeOfPayment && <td className="px-6 py-4 whitespace-nowrap text-sm ">{formatModeOfPaymentDisplay(vendor.modeOfPayment) || '-'}</td>}
                    {visibleColumns.transactionId && <td className="px-6 py-4 whitespace-nowrap text-sm ">{vendor.transactionId || '-'}</td>}
                    {visibleColumns.chequeNo && <td className="px-6 py-4 whitespace-nowrap text-sm ">{vendor.chequeNo || '-'}</td>}
                    {visibleColumns.transactionDetails && <td className="px-6 py-4 whitespace-nowrap text-sm ">{vendor.transactionDetails}</td>}
                    {visibleColumns.bank && <td className="px-6 py-4 whitespace-nowrap text-sm ">{vendor.bank}</td>}
                    {visibleColumns.dateOfTranscation && <td className="px-6 py-4 whitespace-nowrap text-sm ">{formatDateMMMDDYYYY(vendor.dateOfTranscation)}</td>}
                    {visibleColumns.comments && <td className="px-6 py-4 whitespace-nowrap text-sm max-w-xs truncate" title={vendor.comments || ''}>{vendor.comments || '-'}</td>}
                    {visibleColumns.actions && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(vendor.id)}
                            className="text-blue-600 hover:text-blue-900 font-medium"
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDelete(vendor.id)}
                            className="text-red-600 hover:text-red-900 font-medium"
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                    ))
                  )}
                </tbody>
              </table>
              </div>
            </div>
            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              itemsPerPage={itemsPerPage}
              totalItems={filteredData.length}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        </Grid>
      )}

      {/* Add/Edit Vendor Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 m-0 overflow-hidden" style={{marginTop: 0}}>
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={handleClose}
            ></div>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left shadow-xl transform transition-all modal-card app-modal-panel">
              <form onSubmit={handleSubmit} className="flex h-full min-h-0 flex-col">
                {/* Header */}
                <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {isEditMode ? 'Edit Vendor' : 'Add Vendor'}
                  </h3>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Form content */}
                <div className="min-h-0 flex-1 overflow-y-auto bg-white px-6 py-4">
                  {/* 1. Date & Vendor */}
                  <ModalSection>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Date <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          name="date"
                          value={formData.date}
                          onChange={handleInputChange}
                          max={getTodayDate()}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Vendor Name <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="vendorName"
                          value={formData.vendorName}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        >
                          <option value="">Select Vendor</option>
                          {modalVendorOptions.map((vendor) => (
                            <option key={vendor} value={vendor}>
                              {vendor}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Mode of Payment
                        </label>
                        <select
                          name="modeOfPayment"
                          value={formData.modeOfPayment}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        >
                          <option value="">Select</option>
                          <option value="Cash">Cash</option>
                          <option value="Cheque">Cheque</option>
                          <option value="UPI">UPI</option>
                          <option value="Online Bank">Online Bank</option>
                        </select>
                      </div>
                    </div>
                  </ModalSection>

                  {/* 2. Reference Details */}
                  <ModalSection>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Invoice No.
                        </label>
                        <input
                          type="text"
                          name="invoiceNo"
                          value={formData.invoiceNo}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder=""
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          GST Number
                        </label>
                        <input
                          type="text"
                          name="gstNo"
                          value={formData.gstNo}
                          onChange={handleInputChange}
                          maxLength={15}
                          placeholder=""
                          autoComplete="off"
                          spellCheck={false}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-mono uppercase"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          GST (%) 
                        </label>
                        <input
                          type="text"
                          name="gstPercentage"
                          value={formData.gstPercentage || ''}
                          onChange={handleInputChange}
                          placeholder=""
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Invoice Status <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="invoiceStatus"
                          value={formData.invoiceStatus}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        >
                          <option value="">Select</option>
                          <option value="Paid">Paid</option>
                          <option value="Overdue">Overdue</option>
                          <option value="Pending">Pending</option>
                        </select>
                      </div>
                    </div>
                  </ModalSection>

                  {/* 3. Payment Details */}
                  <ModalSection>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      {(formData.modeOfPayment === 'Cheque' || formData.modeOfPayment === 'UPI' || formData.modeOfPayment === 'Online Bank') && (
                        <div
                          className={
                            formData.modeOfPayment === 'Cheque'
                              ? 'md:col-span-2 lg:col-span-2'
                              : ''
                          }
                        >
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {formData.modeOfPayment === 'Cheque' ? 'Credit Note' : 'Transaction ID'} <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="transactionId"
                            value={formData.transactionId}
                            onChange={handleInputChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            placeholder=""
                          />
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Transaction Details
                        </label>
                        <input
                          type="text"
                          name="transactionDetails"
                          value={formData.transactionDetails}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder=""
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Bank <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="bank"
                          value={formData.bank}
                          onChange={handleBankChange}
                          required={formData.bank !== ADD_BANK_VALUE}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        >
                          <option value="">Select</option>
                          {bankOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                          <option value={ADD_BANK_VALUE}>Add Bank</option>
                        </select>
                      </div>

                      {formData.bank === ADD_BANK_VALUE && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Bank Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="customBank"
                            value={formData.customBank}
                            onChange={handleInputChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            placeholder="Enter bank name"
                          />
                        </div>
                      )}
                    </div>
                  </ModalSection>

                  {/* 4. Contact & Supply */}
                  <ModalSection>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Contact Person
                        </label>
                        <input
                          type="text"
                          name="contactName"
                          value={formData.contactName}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder=""
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Contact Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          name="contactNumber"
                          value={formData.contactNumber}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder=""
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Material Supplied <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="materialSupplied"
                          value={formData.materialSupplied}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        >
                          <option value="">Select</option>
                          {materialSuppliedOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Supply Type <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="supplyType"
                          value={formData.supplyType}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        >
                          <option value="">Select</option>
                          <option value="In">In</option>
                          <option value="Out">Out</option>
                        </select>
                      </div>
                    </div>
                  </ModalSection>

                  {/* 5. Quantity & Amount */}
                  <ModalSection>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Total Amount <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          name="totalAmount"
                          value={formData.totalAmount}
                          onChange={handleInputChange}
                          onKeyDown={handleNumberInput}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder=""
                        />
                        <AmountWithGstHint gstPercentage={formData.gstPercentage} withGST={formData.withGST} />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Advance (₹)
                        </label>
                        <input
                          type="number"
                          name="advance"
                          value={formData.advance}
                          onChange={handleInputChange}
                          onKeyDown={handleNumberInput}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder=""
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Balance (₹)
                        </label>
                        <input
                          type="text"
                          name="balance"
                          value={formData.balance || ''}
                          onChange={handleInputChange}
                          onKeyDown={handleNumberInput}
                          readOnly
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-gray-100 text-gray-600 cursor-not-allowed"
                          placeholder=""
                        />
                      </div>
                    </div>

                    <div className="mt-2 rounded-md bg-green-50 px-4 py-3 text-sm text-teal-800">
                      <span className="mr-1">ⓘ</span>
                      Balance is calculated as: Total Amount + GST Amount – Advance
                    </div>
                  </ModalSection>

                  {/* 6. Additional Details */}
                  <ModalSection>
                    <div className="grid grid-cols-1 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Comments
                        </label>
                        <textarea
                          name="comments"
                          value={formData.comments}
                          onChange={handleInputChange}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder="Optional notes"
                        />
                      </div>
                    </div>
                  </ModalSection>
                </div>

                {/* Footer */}
                <div className="flex flex-shrink-0 justify-end space-x-3 border-t border-gray-200 bg-gray-50 px-6 py-3">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    style={{ backgroundColor: '#009688' }}
                  >
                    {isEditMode ? 'Update' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Background overlay */}
          <div 
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            onClick={handleCancelDelete}
          ></div>

          {/* Confirmation Dialog */}
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 transform transition-all">
            <div className="p-6">
              {/* Warning Icon */}
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
                <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              
              {/* Message */}
              <h3 className="text-lg font-medium text-gray-900 text-center mb-2">
                Confirm Delete
              </h3>
              <p className="text-sm text-gray-500 text-center mb-6">
                Are you sure you want to delete this vendor record? This action cannot be undone.
              </p>
              
              {/* Action buttons */}
              <div className="flex gap-3 justify-center">
                <button
                  type="button"
                  onClick={handleCancelDelete}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  className="px-6 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 hover:opacity-90"
                  style={{ backgroundColor: '#dc2626' }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        style={{position: 'absolute', bottom: '10px', left: '10px'}}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ 
            width: '100%', 
            backgroundColor: snackbarSeverity === 'success' ? '#43a047' : '#dc2626', 
            color: '#fff', 
            fontWeight: '400',
            fontSize: '18px' 
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Message */}
      {error && (
        <div className="bg-red-200 border border-red-400 text-red-700 px-4 py-3 rounded" style={{position: 'absolute', bottom: '10px', left: '10px', zIndex: '1000', minWidth: '200px'}}>
          {error}
        </div>
      )}
    </div>
    </>
  );
};

export default Vendors;

